import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

const handleApiError = (error) => {
  const message =
    error.response && error.response.data.message
      ? error.response.data.message
      : error.message || "An unexpected error occurred"
  return message
}

const initialState = {
  states: [],
  state: null,
  cities: [],
  city: null,
  loading: false,
  error: null,
  success: false,
}

// Get all states
export const getAllStates = createAsyncThunk("locations/getAllStates", async (_, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(`${API_URL}/api/locations/states`, config)

    return data.states
  } catch (error) {
    return rejectWithValue(handleApiError(error))
  }
})

// Get state by ID
export const getStateById = createAsyncThunk("locations/getStateById", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(`${API_URL}/api/locations/states/${id}`, config)

    return data.state
  } catch (error) {
    return rejectWithValue(handleApiError(error))
  }
})

// Create state
export const createState = createAsyncThunk(
  "locations/createState",
  async (stateData, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(`${API_URL}/api/locations/states`, stateData, config)

      return data
    } catch (error) {
      return rejectWithValue(handleApiError(error))
    }
  },
)

// Update state
export const updateState = createAsyncThunk(
  "locations/updateState",
  async ({ id, stateData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/locations/states/${id}`, stateData, config)

      return data
    } catch (error) {
      return rejectWithValue(handleApiError(error))
    }
  },
)

// Delete state
export const deleteState = createAsyncThunk("locations/deleteState", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.delete(`${API_URL}/api/locations/states/${id}`, config)

    return { id, data }
  } catch (error) {
    return rejectWithValue(handleApiError(error))
  }
})

// Get cities by state
export const getCitiesByState = createAsyncThunk(
  "locations/getCitiesByState",
  async (stateId, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/locations/states/${stateId}/cities`, config)

      return data
    } catch (error) {
      return rejectWithValue(handleApiError(error))
    }
  },
)

// Create city
export const createCity = createAsyncThunk(
  "locations/createCity",
  async ({ stateId, cityData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(`${API_URL}/api/locations/states/${stateId}/cities`, cityData, config)

      return data
    } catch (error) {
      return rejectWithValue(handleApiError(error))
    }
  },
)

// Update city
export const updateCity = createAsyncThunk(
  "locations/updateCity",
  async ({ id, cityData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/locations/cities/${id}`, cityData, config)

      return data
    } catch (error) {
      return rejectWithValue(handleApiError(error))
    }
  },
)

// Delete city
export const deleteCity = createAsyncThunk("locations/deleteCity", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.delete(`${API_URL}/api/locations/cities/${id}`, config)

    return { id, data }
  } catch (error) {
    return rejectWithValue(handleApiError(error))
  }
})

const locationSlice = createSlice({
  name: "locations",
  initialState,
  reducers: {
    resetLocationState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all states
      .addCase(getAllStates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllStates.fulfilled, (state, action) => {
        state.loading = false
        state.states = action.payload
      })
      .addCase(getAllStates.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get state by ID
      .addCase(getStateById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getStateById.fulfilled, (state, action) => {
        state.loading = false
        state.state = action.payload
      })
      .addCase(getStateById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create state
      .addCase(createState.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createState.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(createState.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update state
      .addCase(updateState.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateState.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(updateState.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete state
      .addCase(deleteState.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteState.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.states = state.states.filter((state) => state._id !== action.meta.arg)
      })
      .addCase(deleteState.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get cities by state
      .addCase(getCitiesByState.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCitiesByState.fulfilled, (state, action) => {
        state.loading = false
        state.cities = action.payload.cities
        state.state = action.payload.state
      })
      .addCase(getCitiesByState.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create city
      .addCase(createCity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCity.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(createCity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update city
      .addCase(updateCity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCity.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(updateCity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete city
      .addCase(deleteCity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCity.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.cities = state.cities.filter((city) => city._id !== action.meta.arg)
      })
      .addCase(deleteCity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { resetLocationState } = locationSlice.actions

export default locationSlice.reducer

